// Token vision from stat blocks. A token's vision profile is "by stat block"
// unless the DM overrides it on the row; the stat block is looked up through
// the DM's source order (D&D Beyond sheet, Roll20 sheet senses line, an LMC
// creature) and the first source with data wins. No stat block anywhere →
// nothing is written, so the token stays sightless.
import type { Character, Layout } from "./types";

export type VisionSource = "ddb" | "roll20" | "lmc";
export const VISION_SOURCES: { key: VisionSource; label: string }[] = [
  { key: "ddb", label: "D&D Beyond sheet" },
  { key: "roll20", label: "Roll20 (sheet senses / token as dropped)" },
  { key: "lmc", label: "LMC creature" },
];
export const DEFAULT_VISION_SOURCES: VisionSource[] = ["ddb", "roll20", "lmc"];
export function visionSources(layout: Layout): VisionSource[] {
  const raw = Array.isArray(layout.visionSources) ? (layout.visionSources as unknown[]) : [];
  const picked = raw.filter((k): k is VisionSource => k === "ddb" || k === "roll20" || k === "lmc");
  return picked.length ? [...picked, ...DEFAULT_VISION_SOURCES.filter((k) => !picked.includes(k))] : DEFAULT_VISION_SOURCES;
}

export type VisionEffect = "Dimming" | "Nocturnal" | "";
export const visionEffect = (layout: Layout): VisionEffect => (layout.visionEffect === "Nocturnal" || layout.visionEffect === "" ? layout.visionEffect : "Dimming");

/** What gets written: sight on/off, night vision distance (0 = off), effect. */
export interface TokenVision { sight: boolean; night: number; effect: VisionEffect }

/** The DM's override for a row.
 *
 * Devil's Sight, blindsight and truesight were missing, and a player who HAS
 * Devil's Sight could not be given it (reported 2026-10-01). The senses parser
 * already understood all three; only the picker did not offer them.
 *
 * Roll20 draws one thing - a night vision distance and whether it dims - so the
 * three new modes differ from plain night vision in exactly the way the rules
 * do: they do NOT dim. Darkvision shows darkness as dim grey; Devil's Sight and
 * truesight see in darkness NORMALLY, and blindsight is not sight at all, so
 * dimming it would be meaningless. */
export type VisionMode = "statblock" | "vision" | "night" | "devils" | "blindsight" | "truesight" | "none";
export interface VisionProfile { mode: VisionMode; night?: number }
export const PROFILE_LABEL: Record<VisionMode, string> = { statblock: "by stat block", vision: "vision", night: "night vision", devils: "devil’s sight", blindsight: "blindsight", truesight: "truesight", none: "no vision" };
/** The modes that carry a range, and what to propose when one is chosen. */
export const RANGED_MODES = { night: 60, devils: 120, blindsight: 30, truesight: 120 } as const;
export const isRangedMode = (m: VisionMode): m is keyof typeof RANGED_MODES => m in RANGED_MODES;
/** Darkvision dims; seeing normally in the dark does not. */
export const modeDims = (m: VisionMode) => m === "night" || m === "statblock";

/** "darkvision 120 ft., blindsight 10 ft., passive Perception 14" → ranges. */
export function parseSenses(line: string): { darkvision: number; blindsight: number; truesight: number; tremorsense: number } {
  const out = { darkvision: 0, blindsight: 0, truesight: 0, tremorsense: 0 };
  for (const m of String(line || "").matchAll(/(darkvision|blindsight|truesight|tremorsense)\s*(\d+)/gi)) out[m[1].toLowerCase() as keyof typeof out] = Math.max(out[m[1].toLowerCase() as keyof typeof out], Number(m[2]));
  return out;
}
/** Night vision as Roll20 can draw it: darkvision, or truesight, or blindsight (the nearest it has). */
const nightFromSenses = (s: ReturnType<typeof parseSenses>) => Math.max(s.darkvision, s.truesight, s.blindsight);

/** What each source knows about a token. */
export interface StatBlockInputs { character?: Character | null; roll20Senses?: string | null; roll20Token?: { sight: boolean; night: number; effect: string } | null; lmcSenses?: string | null; grant?: VisionGrant | null }
/** Night vision lent by an aura (Twilight Sanctuary's dim light with Eyes of
 * Night): kept in layout.visionGrants by target key, withdrawn with the aura. */
export interface VisionGrant { night: number; casterId: string; casterName: string; auraKey: string; auraName: string }
export const grantKeyFor = (casterId: string, auraKey: string) => `${casterId}:${auraKey}`;

export function visionFromStatBlock(layout: Layout, inputs: StatBlockInputs): { vision: TokenVision; source: VisionSource } | null {
  const effect = visionEffect(layout);
  for (const source of visionSources(layout)) {
    if (source === "ddb" && inputs.character) {
      // A sheet always sees; darkvision (or any "…vision" range) is its night vision.
      let night = 0;
      for (const v of inputs.character.vision || []) { const r = Number(v.range || 0); if (r > night && /vision|sight/i.test(String(v.type || ""))) night = r; }
      return { vision: { sight: true, night, effect }, source };
    }
    if (source === "roll20") {
      // A senses line when the sheet has one (OGL sheet); on the 2024 sheet a
      // monster's stat block is not local, but the compendium sets the token's
      // vision at drop time — that token vision is the stat block.
      if (inputs.roll20Senses) { const s = parseSenses(inputs.roll20Senses); return { vision: { sight: true, night: nightFromSenses(s), effect }, source }; }
      const t = inputs.roll20Token;
      if (t && (t.sight || t.night > 0)) return { vision: { sight: true, night: t.night, effect: t.night > 0 && t.effect ? (t.effect as VisionEffect) : effect }, source };
    }
    if (source === "lmc" && inputs.lmcSenses) {
      const s = parseSenses(inputs.lmcSenses);
      return { vision: { sight: true, night: nightFromSenses(s), effect }, source };
    }
  }
  return null;
}

/** The table's rule for Blinded (Roll20 options → Blindness condition rules):
 * remove the token's sight, reduce it to 5 ft, or leave it alone. Blindsight
 * or tremorsense on the sheet still work: the token keeps sight with night
 * vision capped at that range (Roll20 has no such sense). */
export type BlindRule = "remove" | "five" | "none";
export const BLIND_RULES: { key: BlindRule; label: string; hint: string }[] = [
  { key: "remove", label: "remove vision", hint: "A blinded token sees nothing (unless the sheet lists blindsight or tremorsense)." },
  { key: "five", label: "reduce vision to 5 ft", hint: "A blinded token keeps sight with night vision 5 ft — it perceives what is adjacent." },
  { key: "none", label: "no effect", hint: "Blinded changes nothing on the token." },
];
export const blindRule = (layout: Layout): BlindRule => (layout.blindRule === "five" || layout.blindRule === "none" ? layout.blindRule : "remove");
export const isBlinded = (c: Character | null | undefined) => Boolean(c && (c.conditions || []).some((x) => /blind/i.test(typeof x === "string" ? x : String(x.name || x.condition || ""))));
export function blindedOverride(c: Character | null | undefined, effect: VisionEffect, rule: BlindRule = "remove"): { vision: TokenVision; via: string } | null {
  if (!c || rule === "none" || !isBlinded(c)) return null;
  let sense = 0;
  for (const v of c.vision || []) { const r = Number(v.range || 0); if (r > sense && /blindsight|tremorsense/i.test(String(v.type || ""))) sense = r; }
  if (sense > 0) return { vision: { sight: true, night: sense, effect }, via: `blinded — blindsight/tremorsense ${sense} ft` };
  return rule === "five" ? { vision: { sight: true, night: 5, effect }, via: "blinded — 5 ft (table rule)" } : { vision: { sight: false, night: 0, effect }, via: "blinded" };
}

/** The row's profile applied over the stat block; null = write nothing. */
export function resolveVision(layout: Layout, profile: VisionProfile | undefined, inputs: StatBlockInputs): { vision: TokenVision; via: string } | null {
  const effect = visionEffect(layout);
  const blind = blindedOverride(inputs.character, effect, blindRule(layout));
  if (blind) return blind;
  const mode = profile?.mode || "statblock";
  // A lent night vision raises whatever the row would have had (never lowers it).
  const lend = (r: { vision: TokenVision; via: string } | null): { vision: TokenVision; via: string } | null => {
    const g = inputs.grant; if (!g || g.night <= 0) return r;
    const base = r ?? { vision: { sight: true, night: 0, effect }, via: "no stat block" };
    if (!base.vision.sight || base.vision.night >= g.night) return r;
    return { vision: { ...base.vision, night: g.night }, via: `${g.auraName} (${g.casterName}) lends night vision ${g.night} ft over ${base.via}` };
  };
  if (mode === "none") return { vision: { sight: false, night: 0, effect }, via: "no vision (row)" };
  if (mode === "vision") return lend({ vision: { sight: true, night: 0, effect }, via: "vision only (row)" });
  if (isRangedMode(mode)) {
    const range = Math.max(0, Number(profile?.night ?? RANGED_MODES[mode]));
    // Seeing normally in the dark is written WITHOUT the dimming effect, which
    // is the whole difference between darkvision and the other three.
    return lend({ vision: { sight: true, night: range, effect: modeDims(mode) ? effect : "" }, via: `${PROFILE_LABEL[mode]} ${range} ft (row)` });
  }
  const found = visionFromStatBlock(layout, inputs);
  return lend(found ? { vision: found.vision, via: `${VISION_SOURCES.find((s) => s.key === found.source)?.label || found.source}` } : null);
}

export const describeVision = (v: TokenVision) => v.sight ? (v.night > 0 ? `vision · night vision ${v.night} ft${v.effect ? ` (${v.effect.toLowerCase()})` : ""}` : "vision") : "no vision";
